/* eslint-disable @typescript-eslint/no-explicit-any */

export interface ChatSearchCriteria {
  checkInDate?: string;
  checkOutDate?: string;
  flexibleDate?: boolean | string;
  numRooms?: number | string;
  numAdultsPerRoom?: number | string;
  numChildrenPerRoom?: number | string;
  childrenAges?: Array<number | string> | string;
  useRewardsPoints?: boolean;
  clusterCode?: string;
  corporateCode?: string;
  groupCode?: string;
}

export interface FallbackSearchOptions {
  startDate?: string;
  endDate?: string;
  quantity?: number | string;
  childAges?: Array<number | string> | null;
}

export interface FallbackAvailabilityRequest {
  checkInDate?: string;
  checkOutDate?: string;
  flexibleDate?: boolean | string;
  numRooms?: number | string;
  numAdultsPerRoom?: number | string;
  numChildrenPerRoom?: number | string;
  childrenAges?: Array<number | string> | string;
  clusterCode?: string;
  corporateCode?: string;
  groupCode?: string;
}

export interface BuildChatAvailabilityUrlParams {
  baseUrl: string;
  propertyCode: string | number;
  chatSearchCriteria?: ChatSearchCriteria;
  fallbackSearchOptions?: FallbackSearchOptions;
  fallbackAvailabilityRequest?: FallbackAvailabilityRequest;
  fallbackUseRewardsPoints?: boolean;
  fallbackFlexibleDate?: boolean;
  fallbackNumAdultsPerRoom?: number | string;
  forceRateCalendar?: boolean;
}

const toBool = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return undefined;
};

const formatDateForUrl = (iso: string): string => {
  const normalized = (iso || '').slice(0, 10);
  const [year, month, day] = normalized.split('-');
  if (year && month && day) return `${month}/${day}/${year}`;
  return iso;
};

const toAgeArray = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return value.map(age => parseInt(String(age), 10)).filter(age => !isNaN(age));
  }
  if (typeof value === 'string' && value) {
    return value
      .split(',')
      .map(age => parseInt(String(age), 10))
      .filter(age => !isNaN(age));
  }
  return [];
};

export const buildChatAvailabilityUrl = ({
  baseUrl,
  propertyCode,
  chatSearchCriteria,
  fallbackSearchOptions,
  fallbackAvailabilityRequest,
  fallbackUseRewardsPoints,
  fallbackFlexibleDate,
  fallbackNumAdultsPerRoom,
  forceRateCalendar = false,
}: BuildChatAvailabilityUrlParams): string => {
  const todayISO = new Date().toISOString().slice(0, 10);
  const rawFrom =
    chatSearchCriteria?.checkInDate ??
    fallbackAvailabilityRequest?.checkInDate ??
    fallbackSearchOptions?.startDate ??
    todayISO;
  const rawTo =
    chatSearchCriteria?.checkOutDate ??
    fallbackAvailabilityRequest?.checkOutDate ??
    fallbackSearchOptions?.endDate ??
    todayISO;

  const fromDate = formatDateForUrl(rawFrom);
  const toDate = formatDateForUrl(rawTo);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.round((new Date(rawTo).getTime() - new Date(rawFrom).getTime()) / msPerDay);
  const lengthOfStay = Math.max(diff, 1);

  const childAgesArray = toAgeArray(
    chatSearchCriteria?.childrenAges ??
      fallbackAvailabilityRequest?.childrenAges ??
      fallbackSearchOptions?.childAges ??
      []
  );
  const childrenCount = Number(
    chatSearchCriteria?.numChildrenPerRoom ?? fallbackAvailabilityRequest?.numChildrenPerRoom ?? childAgesArray.length
  );
  const numberOfRooms = Number(
    chatSearchCriteria?.numRooms ?? fallbackAvailabilityRequest?.numRooms ?? fallbackSearchOptions?.quantity ?? 1
  );
  const numberOfAdults = Number(
    chatSearchCriteria?.numAdultsPerRoom ??
      fallbackAvailabilityRequest?.numAdultsPerRoom ??
      fallbackNumAdultsPerRoom ??
      1
  );
  const useRewards = chatSearchCriteria?.useRewardsPoints ?? fallbackUseRewardsPoints ?? false;
  const isFlexible =
    forceRateCalendar ||
    toBool(chatSearchCriteria?.flexibleDate) ||
    toBool(fallbackAvailabilityRequest?.flexibleDate) ||
    fallbackFlexibleDate ||
    false;

  const params = new URLSearchParams({
    propertyCode: String(propertyCode),
    fromDate,
    toDate,
    flexibleDateSearch: isFlexible ? 'true' : 'false',
    lengthOfStay: String(lengthOfStay),
    numberOfRooms: String(isNaN(numberOfRooms) ? 1 : numberOfRooms),
    numberOfAdults: String(isNaN(numberOfAdults) ? 1 : numberOfAdults),
    childrenCount: String(isNaN(childrenCount) ? 0 : childrenCount),
    childrenAges: childAgesArray.join(','),
    useRewardsPoints: useRewards ? 'true' : 'false',
  });

  if (isFlexible) {
    params.set('isRateCalendar', 'true');
    params.set('flexibleDateSearch', 'true');
  }

  if (!useRewards) {
    const clusterCode = chatSearchCriteria?.clusterCode ?? fallbackAvailabilityRequest?.clusterCode;
    const corporateCode = chatSearchCriteria?.corporateCode ?? fallbackAvailabilityRequest?.corporateCode;
    const groupCode = chatSearchCriteria?.groupCode ?? fallbackAvailabilityRequest?.groupCode;

    if (corporateCode || clusterCode === 'corp') {
      params.set('clusterCode', 'corp');
      if (corporateCode) params.set('corporateCode', corporateCode);
    } else if (clusterCode === 'AAA' || clusterCode === 'aaa') {
      params.set('clusterCode', 'aaa');
    } else if (clusterCode === 'GOV' || clusterCode === 'gov') {
      params.set('clusterCode', 'gov');
    } else if (clusterCode === 'S9R' || clusterCode === 's9r') {
      params.set('clusterCode', 'S9R');
    } else if (groupCode) {
      params.set('clusterCode', 'grp');
      params.set('groupCode', groupCode);
    }
  }

  return `${baseUrl}?${params.toString()}`;
};
